import { AxiosPromise, AxiosResponse } from 'axios';
import { useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { GetVaccinesParams, PaginatedResponse, VaccineListItem, VaccinePetsListItem } from 'shared/api';

import { useDebouncedSearch } from './useDebouncedSearch';

type VaccineItem = VaccineListItem | VaccinePetsListItem;

type UseVaccineSearchOptions = {
  fetchFn: (params: GetVaccinesParams) => AxiosPromise<PaginatedResponse<VaccineItem>>;
  queryKey: string;
  limit?: number;
  enabled?: boolean;
};

const VACCINE_SEARCH_LIMIT = 20;

export function useVaccineSearch({
  fetchFn,
  queryKey,
  limit = VACCINE_SEARCH_LIMIT,
  enabled = true,
}: UseVaccineSearchOptions) {
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedSearch(searchText, 400);

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery<
    AxiosResponse<PaginatedResponse<VaccineItem>>
  >(
    [queryKey, debouncedSearch],
    ({ pageParam = 1 }) =>
      fetchFn({
        filter: debouncedSearch || undefined,
        page: pageParam,
        limit,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    {
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage.data;
        return pagination.hasNext ? pagination.page + 1 : undefined;
      },
      enabled,
      refetchOnWindowFocus: false,
      staleTime: 0,
      retry: 1,
    },
  );

  const options = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.data.data);
  }, [data]);

  const isSearching = searchText.trim() !== debouncedSearch || (isFetching && !isFetchingNextPage);

  return {
    options,
    setSearchText,
    isLoading,
    isSearching,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
  };
}
